/**
 * Employee Management Service
 * Handles all API calls for employee management operations
 */

import baseAPIService from '@/services/baseapiservice';
import env from '@/config/env';
import { isRequestCanceled } from '@/utils/requestCancel';

interface UserFilters {
  active?: boolean;
  username?: string;
  role?: string;
  fromDate?: string;
  department?: number;
  departmentCode?: string; // Department by code (dashboard "Headcount by Department" drill-down)
  designation?: number;
  employeeCode?: string;
  employeeName?: string;
  status?: string; // Employee status filter
  employeeType?: number; // Employee type filter
  employeeTypeName?: string; // Employee type by name (dashboard "By Employee Type" drill-down)
  hierarchyType?: number; // Hierarchy type filter
  boundaryType?: number; // Boundary type filter
  tenantId?: string; // Tenant/ULB filter (for state-level users)
  udaId?: number | null; // Selected UDA id (additive; sent alongside tenantId, omitted when null)
  activeAccounts?: boolean; // Filter: active accounts (enabled, not locked, password not expired)
  anyAssignment?: boolean; // Filter: employees holding any assignment record (current or lapsed)
  activeAssignment?: boolean; // Filter: employees whose assignment currently covers today
  noAssignment?: boolean; // Filter: employees without any assignment
  expiredAssignment?: boolean; // Filter: employees whose assignment has expired (no current active one)
  withJurisdiction?: boolean; // Filter: employees with at least one jurisdiction
  noJurisdiction?: boolean; // Filter: employees without any jurisdiction
  /** Filter: employees holding at least one live IN-CHARGE posting. */
  holdingIncharge?: boolean;
  neverLoggedIn?: boolean; // Filter: employees who never logged in successfully
  loggedInToday?: boolean; // Filter: employees with a successful login today (IST)
  retiredWithAssignment?: boolean; // Filter: retired but still holding an active assignment
  retiredActiveLogin?: boolean; // Filter: retired but eg_user still active (can log in)
  // Account Health & Reachability cards
  locked?: boolean; // Filter: accounts flagged locked
  pwdExpired?: boolean; // Filter: password expiry date has passed
  noEmail?: boolean; // Filter: no email on record (can't receive a reset link)
  noMobile?: boolean; // Filter: no mobile on record (can't receive OTP)
  searchText?: string; // Free-text search (name / code / username / mobile / email) — server-side
  page?: number; // 1-based page number for server-side pagination
  pageSize?: number; // Rows per page (default 20 server-side, max 1000)
}

/** Pagination envelope returned alongside the rows by POST /employee/list. */
interface EmployeePage {
  data: UserData[];
  permissions?: any;
  total: number;
  activeTotal: number;
  inactiveTotal: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface Stats {
  total_users: number;
  active_users: number;
  inactive_users: number;
}

interface UserData {
  user_id: number;
  username: string;
  user_fullname: string;
  active: boolean;
  emailid: string;
  mobilenumber: string;
  user_type: string;
  role_name: string;
  role_description: string;
  designation: string;
  designation_code: string;
  department_id?: number;
  department_name?: string;
  department_code?: string;
  position_id?: number;
  position_name?: string;
  is_primary_assignment?: boolean;
  fromdate: string;
  todate: string;
  region: string;
  district: string;
  ulb_name: string;
  ulb_code: string;
  ulb_grade: string;
  boundary_type_name?: string;
  boundary_name?: string;
  boundary_code?: string;
  hierarchy_type_name?: string;
  createddate: string;
  lastmodifieddate: string;
}

/**
 * Session cache for the UDA master (see getAllUDAs). Module-level so every consumer
 * of the singleton service below shares it; `udaListPromise` collapses concurrent
 * first-callers into a single request.
 */
let udaListCache: any[] | null = null;
let udaListPromise: Promise<any[]> | null = null;

class EmpManagementService {
  /**
   * Get ONE PAGE of employees with their roles and assignments.
   * The backend paginates with LIMIT/OFFSET — pass filters.page (1-based) and
   * filters.pageSize; the response carries the total so the grid can render
   * page counts without ever downloading the whole list.
   * @param filters - Optional filters (active, department, designation, employeeCode, employeeName, employeeType) + page/pageSize
   * @returns Promise with the page rows, permissions and pagination info
   */
  async getAllEmployees(
    filters?: UserFilters,
    /**
     * Lets the caller cancel a request that has been superseded — the employee
     * dashboard changes scope faster than this query answers, and an abandoned
     * reply arriving last used to overwrite the current scope's rows.
     */
    opts?: { signal?: AbortSignal },
  ): Promise<EmployeePage> {
    try {
      const payload: any = {};

      if (filters?.active !== undefined) {
        payload.active = filters.active;
      }
      if (filters?.department !== undefined) {
        payload.department = filters.department;
      }
      if (filters?.departmentCode) {
        payload.departmentCode = filters.departmentCode;
      }
      if (filters?.designation !== undefined) {
        payload.designation = filters.designation;
      }
      if (filters?.employeeCode) {
        payload.employeeCode = filters.employeeCode;
      }
      if (filters?.employeeName) {
        payload.employeeName = filters.employeeName;
      }
      if (filters?.activeAccounts) {
        payload.activeAccounts = true;
      }
      if (filters?.anyAssignment) {
        payload.anyAssignment = true;
      }
      if (filters?.activeAssignment) {
        payload.activeAssignment = true;
      }
      if (filters?.noAssignment) {
        payload.noAssignment = true;
      }
      if (filters?.expiredAssignment) {
        payload.expiredAssignment = true;
      }
      if (filters?.withJurisdiction) {
        payload.withJurisdiction = true;
      }
      if (filters?.noJurisdiction) {
        payload.noJurisdiction = true;
      }
      // Must be listed here or it is silently dropped: this payload is an explicit
      // allowlist, so an unknown filter reaches the server as no filter at all — which
      // is why the In-charge card was returning every employee.
      if (filters?.holdingIncharge) {
        payload.holdingIncharge = true;
      }
      if (filters?.neverLoggedIn) {
        payload.neverLoggedIn = true;
      }
      if (filters?.loggedInToday) {
        payload.loggedInToday = true;
      }
      if (filters?.retiredWithAssignment) {
        payload.retiredWithAssignment = true;
      }
      if (filters?.retiredActiveLogin) {
        payload.retiredActiveLogin = true;
      }
      if (filters?.locked) {
        payload.locked = true;
      }
      if (filters?.pwdExpired) {
        payload.pwdExpired = true;
      }
      if (filters?.noEmail) {
        payload.noEmail = true;
      }
      if (filters?.noMobile) {
        payload.noMobile = true;
      }
      if (filters?.employeeType !== undefined) {
        payload.employeeType = filters.employeeType;
      }
      if (filters?.employeeTypeName) {
        payload.employeeTypeName = filters.employeeTypeName;
      }
      if (filters?.hierarchyType !== undefined) {
        payload.hierarchyType = filters.hierarchyType;
      }
      if (filters?.boundaryType !== undefined) {
        payload.boundaryType = filters.boundaryType;
      }
      if (filters?.tenantId) {
        payload.tenantId = filters.tenantId;
      }
      // Additive: carry the selected UDA id alongside tenantId (omitted when null —
      // i.e. "All UDAs" or legacy ULB tenants that have no UDA).
      if (filters?.udaId !== undefined && filters?.udaId !== null) {
        payload.udaId = filters.udaId;
      }
      if (filters?.searchText) {
        payload.searchText = filters.searchText;
      }

      // Pagination — always sent so the server never falls back to a full scan.
      const page = filters?.page && filters.page > 0 ? filters.page : 1;
      const pageSize = filters?.pageSize && filters.pageSize > 0 ? filters.pageSize : 20;
      payload.page = page;
      payload.pageSize = pageSize;

      const response = await baseAPIService.post('employee-management/employee/list', payload, opts?.signal ? { signal: opts.signal } : undefined);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }

      // Extract permissions from response
      const permissions = response.data?.perm;

      // Response shape is { rows, total, page, pageSize, totalPages }. A plain
      // array is still accepted so an older/unpaginated backend keeps working.
      const responseData = response.data?.data;
      if (Array.isArray(responseData)) {
        const activeTotal = responseData.filter((r: any) => r?.active !== false).length;
        return {
          data: responseData,
          permissions,
          total: responseData.length,
          activeTotal,
          inactiveTotal: responseData.length - activeTotal,
          page: 1,
          pageSize: responseData.length || pageSize,
          totalPages: 1,
        };
      }

      const rows = Array.isArray(responseData?.rows) ? responseData.rows : [];
      const total = typeof responseData?.total === 'number' ? responseData.total : rows.length;
      return {
        data: rows,
        permissions,
        total,
        activeTotal: responseData?.activeTotal ?? 0,
        inactiveTotal: responseData?.inactiveTotal ?? 0,
        page: responseData?.page ?? page,
        pageSize: responseData?.pageSize ?? pageSize,
        totalPages: responseData?.totalPages ?? Math.max(1, Math.ceil(total / pageSize)),
      };
    } catch (error) {
      // A superseded request was aborted on purpose — not a failure to report.
      if (!isRequestCanceled(error)) console.error('Error fetching employees:', error);
      throw error;
    }
  }

  /**
   * Counts powering the 3 summary cards (total / no-assignment / no-jurisdiction).
   * Hits the dedicated stats endpoint instead of counting client-side.
   */
  async getEmployeeStats(tenantId?: string): Promise<{ total_employees: number; no_assignment: number; no_jurisdiction: number }> {
    const url = tenantId
      ? `employee-management/employee/stats?tenantId=${encodeURIComponent(tenantId)}`
      : 'employee-management/employee/stats';
    const response: any = await baseAPIService.get(url);
    if (response.data?.status !== 200) {
      throw new Error(response.data?.message || 'Failed to fetch employee stats');
    }
    return response.data?.data || { total_employees: 0, no_assignment: 0, no_jurisdiction: 0 };
  }

  /**
   * Get employee statistics for dashboard cards
   * @returns Promise with Stats object
   */
  async getUserStats(): Promise<Stats> {
    try {
      // Counts come from the paginated list envelope (data holds only one page),
      // so ask for the smallest possible page and read the totals off it.
      const page = await this.getAllEmployees({ pageSize: 1 });
      return {
        total_users: page.total,
        active_users: page.activeTotal,
        inactive_users: page.inactiveTotal,
      };
    } catch (error) {
      console.error('Error fetching employee stats:', error);
      throw error;
    }
  }

  /**
   * Get detailed information for a specific employee
   * @param userId - Employee ID
   * @param tenantId - UDA tenant of the row that was clicked. Employee ids are per-UDA
   *                   and a few repeat across two UDAs, so this disambiguates the lookup.
   * @returns Promise with detailed employee object including assignments and jurisdictions
   */
  async getUserById(userId: number, tenantId?: string | null): Promise<any> {
    try {
      const qs = tenantId ? `?tenantId=${encodeURIComponent(String(tenantId))}` : '';
      const response = await baseAPIService.get(`employee-management/employee/${userId}${qs}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      const employee = response.data?.data || response.data;
      // "Employee not found" comes back with status 200 and data: [] (see
      // standardMessages NOT_FOUND). Without this guard the empty array flows
      // through as a truthy "employee" and renders a hollow profile panel.
      if (!employee || Array.isArray(employee) || !employee.id) {
        throw new Error(response.data?.message || 'Employee not found.');
      }
      let assignments = employee.assignments;
      let jurisdictions = employee.jurisdictions;

      return {
        ...employee,
        assignments: assignments || [],
        jurisdictions: jurisdictions || []
      };
    } catch (error) {
      console.error(`Error fetching employee ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Get all ULBs for employee publishing dropdown
   * @returns Promise with array of ULBs
   */
  async getAllULBs(): Promise<any[]> {
    try {
      const response = await baseAPIService.get('erp-admin/tenant');
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching ULBs:', error);
      throw error;
    }
  }

  /**
   * Get the UDA master list for the "Select UDA" dropdown (Phase 1).
   * Sourced from ds_dtcp.ds_uda_lst_t; each row carries uda_id, uda_cd, uda_nm and
   * the operational tenant_code ('10001'-'10021'). The form displays uda_nm and
   * persists tenant_code into the existing tenantId field — tenantid stays the
   * operational key; uda_id is dual-written server-side from that code.
   * @returns Promise with array of { uda_id, uda_cd, uda_nm, tenant_code }
   */
  async getAllUDAs(): Promise<any[]> {
    // The UDA master is a fixed 21-row list that cannot change inside a session, but
    // three separate consumers ask for it — the page UDA dropdown (useUdaOptions), the
    // filter bar's own ULB list, and the Add/Edit form — each on mount and again on
    // every scope change. Cache the resolved list and share the in-flight promise so
    // they collapse into one request instead of three or more per screen.
    if (udaListCache) return udaListCache;
    if (!udaListPromise) {
      udaListPromise = (async () => {
        const response = await baseAPIService.get('erp-admin/uda');
        if (response.data?.status != 200) {
          throw new Error(response.data?.message);
        }
        return response.data?.data || response.data || [];
      })()
        .then((list: any[]) => { udaListCache = list; return list; })
        .catch((error: any) => {
          console.error('Error fetching UDAs:', error);
          throw error;
        })
        // A failed fetch must not poison the cache — let the next caller retry.
        .finally(() => { udaListPromise = null; });
    }
    return udaListPromise;
  }

  /**
   * Create a new employee
   * @param userData - Employee data (flattened structure matching EmployeeCtrl expectations)
   * @returns Promise with created employee
   */
  async createUser(userData: any): Promise<any> {
    try {
      // Standardized field names - no fallbacks needed
      // Form uses these exact field names, backend expects these exact field names
      const payload: any = {
        // Basic Info
        code: userData.code,
        name: userData.name,
        username: userData.username || null,
        emailid: userData.emailid || null,
        mobilenumber: userData.mobilenumber || null,
        active: userData.active !== undefined ? userData.active : true,

        // Personal Info
        dob: userData.dob || null,
        gender: userData.gender || null,
        maritalstatus: userData.maritalstatus || null,
        fathersname: userData.fathersname || null,
        nativeplace: userData.nativeplace || null,
        bloodgroup: userData.bloodgroup || null,
        mothertongue: userData.mothertongue || null,
        religion: userData.religion || null,
        community: userData.community || null,
        category: userData.category || null,
        physicallyhandicapped: userData.physicallyhandicapped || false,
        medicalreportavailable: userData.medicalreportavailable || false,

        // Identification & Documents
        identificationmark: userData.identificationmark || null,
        pan: userData.pan || null,
        aadhaarnumber: userData.aadhaarnumber || null,
        passportnumber: userData.passportnumber || null,
        gpfnumber: userData.gpfnumber || null,
        cfmsid: userData.cfmsid || null,

        // Bank Details
        bank: userData.bank || null,
        branch: userData.branch || null,
        accountnumber: userData.accountnumber || null,
        ifsccode: userData.ifsccode || null,

        // Additional Info
        emergencyphone: userData.emergencyphone || null,

        // Address (permanent + correspondence)
        permanentaddress: userData.permanentaddress || null,
        permanentcity: userData.permanentcity || null,
        permanentpincode: userData.permanentpincode || null,
        correspondenceaddress: userData.correspondenceaddress || null,
        correspondencecity: userData.correspondencecity || null,
        correspondencepincode: userData.correspondencepincode || null,

        // Languages
        languagesknown: userData.languagesknown || null,

        // Employment Info
        employeestatus: userData.employeestatus || null,
        employeetype: userData.employeetype || null,
        employeegroup: userData.employeegroup || null,

        // Recruitment Info
        recruitmentmode: userData.recruitmentmode || null,
        recruitmenttype: userData.recruitmenttype || null,
        recruitmentquota: userData.recruitmentquota || null,

        // Dates
        dateofappointment: userData.dateofappointment || null,
        dateofjoining: userData.dateofjoining || null,
        dateofretirement: userData.dateofretirement || null,
        retirementage: userData.retirementage || null,
        resignationdate: userData.resignationdate || null,
        terminationdate: userData.terminationdate || null,

        // Documents
        photo: userData.photo || null,
        employeesignature: userData.employeesignature || null,
      };

      const response = await baseAPIService.post('employee-management/employee', payload);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error('Error creating employee:', error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to create employee';
      throw new Error(errorMessage);
    }
  }

  /**
   * Create a new employee with assignments and jurisdictions in a single POST
   * Used by the stepper wizard in create mode
   * @param userData - Employee data (flattened)
   * @param assignments - Array of assignment objects
   * @param jurisdictions - Array of jurisdiction objects
   * @returns Promise with created employee including assignments and jurisdictions
   */
  async createEmployeeWithRelations(
    userData: any,
    assignments: any[] = [],
    jurisdictions: any[] = []
  ): Promise<any> {
    try {
      const payload: any = {
        // Basic Info
        code: userData.code,
        name: userData.name,
        username: userData.username || null,
        emailid: userData.emailid || null,
        mobilenumber: userData.mobilenumber || null,
        active: userData.active !== undefined ? userData.active : true,

        // Personal Info
        dob: userData.dob || null,
        gender: userData.gender || null,
        maritalstatus: userData.maritalstatus || null,
        fathersname: userData.fathersname || null,
        nativeplace: userData.nativeplace || null,
        bloodgroup: userData.bloodgroup || null,
        mothertongue: userData.mothertongue || null,
        religion: userData.religion || null,
        community: userData.community || null,
        category: userData.category || null,
        physicallyhandicapped: userData.physicallyhandicapped || false,
        medicalreportavailable: userData.medicalreportavailable || false,

        // Identification & Documents
        identificationmark: userData.identificationmark || null,
        pan: userData.pan || null,
        aadhaarnumber: userData.aadhaarnumber || null,
        passportnumber: userData.passportnumber || null,
        gpfnumber: userData.gpfnumber || null,
        cfmsid: userData.cfmsid || null,

        // Bank Details
        bank: userData.bank || null,
        branch: userData.branch || null,
        accountnumber: userData.accountnumber || null,
        ifsccode: userData.ifsccode || null,

        // Additional Info
        emergencyphone: userData.emergencyphone || null,

        // Address (permanent + correspondence)
        permanentaddress: userData.permanentaddress || null,
        permanentcity: userData.permanentcity || null,
        permanentpincode: userData.permanentpincode || null,
        correspondenceaddress: userData.correspondenceaddress || null,
        correspondencecity: userData.correspondencecity || null,
        correspondencepincode: userData.correspondencepincode || null,

        // Languages
        languagesknown: userData.languagesknown || null,

        // Employment Info
        employeestatus: userData.employeestatus || null,
        employeetype: userData.employeetype || null,
        employeegroup: userData.employeegroup || null,

        // Recruitment Info
        recruitmentmode: userData.recruitmentmode || null,
        recruitmenttype: userData.recruitmenttype || null,
        recruitmentquota: userData.recruitmentquota || null,

        // Dates
        dateofappointment: userData.dateofappointment || null,
        dateofjoining: userData.dateofjoining || null,
        dateofretirement: userData.dateofretirement || null,
        retirementage: userData.retirementage || null,
        resignationdate: userData.resignationdate || null,
        terminationdate: userData.terminationdate || null,

        // Documents
        photo: userData.photo || null,
        employeesignature: userData.employeesignature || null,

        // Tenant
        tenantId: userData.tenantId || null,

        // Relations - sent in the same payload
        assignments: assignments.map(a => ({
          position: a.position,
          designation: a.designation,
          department: a.department,
          fromdate: a.fromdate,
          todate: a.todate,
          isprimary: a.isprimary ?? false,
          // Budget Classification (fund / function / functionary) is no longer
          // captured in the UI, so it is not sent. The columns stay on
          // ds_emp_asgn_dtl_t and remain NULL for assignments created here.
          grade: a.grade || null
        })),
        jurisdictions: jurisdictions.map(j => ({
          boundary: j.boundary,
          boundarytype: j.boundarytype || null
        }))
      };

      const response = await baseAPIService.post('employee-management/employee', payload);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error('Error creating employee with relations:', error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to create employee';
      throw new Error(errorMessage);
    }
  }

  /**
   * Update an existing employee
   * @param userId - Employee ID
   * @param userData - Updated employee data (flattened structure)
   * @returns Promise with updated employee
   */
  async updateUser(userId: number, userData: any, assignments?: any[], jurisdictions?: any[]): Promise<any> {
    try {
      // Standardized field names - no fallbacks needed
      // Form uses these exact field names, backend expects these exact field names
      const payload: any = {
        id: userId,
        // Basic Info
        code: userData.code,
        name: userData.name,
        username: userData.username || userData.mobilenumber || userData.code,
        emailid: userData.emailid || null,
        mobilenumber: userData.mobilenumber || null,
        active: userData.active !== undefined ? userData.active : true,

        // Personal Info
        dob: userData.dob || null,
        gender: userData.gender || null,
        maritalstatus: userData.maritalstatus || null,
        fathersname: userData.fathersname || null,
        nativeplace: userData.nativeplace || null,
        bloodgroup: userData.bloodgroup || null,
        mothertongue: userData.mothertongue || null,
        religion: userData.religion || null,
        community: userData.community || null,
        category: userData.category || null,
        physicallyhandicapped: userData.physicallyhandicapped || false,
        medicalreportavailable: userData.medicalreportavailable || false,

        // Identification & Documents
        identificationmark: userData.identificationmark || null,
        pan: userData.pan || null,
        aadhaarnumber: userData.aadhaarnumber || null,
        passportnumber: userData.passportnumber || null,
        gpfnumber: userData.gpfnumber || null,
        cfmsid: userData.cfmsid || null,

        // Bank Details
        bank: userData.bank || null,
        branch: userData.branch || null,
        accountnumber: userData.accountnumber || null,
        ifsccode: userData.ifsccode || null,

        // Additional Info
        emergencyphone: userData.emergencyphone || null,

        // Address (permanent + correspondence)
        permanentaddress: userData.permanentaddress || null,
        permanentcity: userData.permanentcity || null,
        permanentpincode: userData.permanentpincode || null,
        correspondenceaddress: userData.correspondenceaddress || null,
        correspondencecity: userData.correspondencecity || null,
        correspondencepincode: userData.correspondencepincode || null,

        // Languages
        languagesknown: userData.languagesknown || null,

        // Employment Info
        employeestatus: userData.employeestatus || null,
        employeetype: userData.employeetype || null,
        employeegroup: userData.employeegroup || null,

        // Recruitment Info
        recruitmentmode: userData.recruitmentmode || null,
        recruitmenttype: userData.recruitmenttype || null,
        recruitmentquota: userData.recruitmentquota || null,

        // Dates
        dateofappointment: userData.dateofappointment || null,
        dateofjoining: userData.dateofjoining || null,
        dateofretirement: userData.dateofretirement || null,
        retirementage: userData.retirementage || null,
        resignationdate: userData.resignationdate || null,
        terminationdate: userData.terminationdate || null,

        // Documents
        photo: userData.photo || null,
        employeesignature: userData.employeesignature || null,

        // Tenant context (for state-level users editing ULB employees)
        tenantId: userData.tenantId || null,
      };

      // Include assignments and jurisdictions if provided. Preserve id for
      // existing rows so the backend can upsert (UPDATE existing, INSERT new,
      // DELETE removed) instead of wipe-and-recreate.
      if (assignments) {
        payload.assignments = assignments.map(a => ({
          id: a.id,                            // preserve to enable in-place UPDATE
          position: a.position,
          designation: a.designation,
          department: a.department,
          fromdate: a.fromdate,
          todate: a.todate,
          isprimary: a.isprimary ?? false,
          // Budget Classification is not captured in the UI — see the create
          // payload above. Omitting it is safe on edit because updateAssignmentMdl
          // COALESCEs those columns, so legacy values survive.
          grade: a.grade || null
        }));
      }
      if (jurisdictions) {
        payload.jurisdictions = jurisdictions.map(j => ({
          id: j.id,                            // preserve to enable in-place UPDATE
          boundary: j.boundary,
          boundarytype: j.boundarytype || null
        }));
      }

      const response = await baseAPIService.put(`employee-management/employee/${userId}`, payload);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error updating employee ${userId}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to update employee';
      throw new Error(errorMessage);
    }
  }

  /**
   * Assign a role to an employee
   * @param employeeId - Employee ID
   * @param roleId - Role ID
   * @returns Promise with assignment result
   */
  async assignRoleToEmployee(employeeId: number, roleId: number): Promise<any> {
    try {
      const response = await baseAPIService.post(`employee-management/employee/${employeeId}/role`, { roleId });
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error assigning role to employee ${employeeId}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to assign role';
      throw new Error(errorMessage);
    }
  }

  /**
   * Delete an employee
   * @param userId - Employee ID
   * @returns Promise with deletion result
   */
  /**
   * Activate an account in one step: sets active = true, clears accountlocked,
   * extends password expiry by a year and drops the failed-attempt counter.
   * State/Regional administrators only (enforced server-side).
   */
  async activateEmployeeAccount(employeeId: number): Promise<any> {
    try {
      const response = await baseAPIService.post(`employee-management/employee/${employeeId}/activate`, {});
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error activating employee account ${employeeId}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to activate account';
      throw new Error(errorMessage);
    }
  }

  async deleteUser(userId: number): Promise<any> {
    try {
      const response = await baseAPIService.delete(`employee-management/employee/${userId}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error deleting employee ${userId}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to delete employee';
      throw new Error(errorMessage);
    }
  }

  /**
   * UDAs this employee works at, with live assignment and jurisdiction counts.
   * An employee holds a separate account per UDA, so this is the list of those.
   */
  async getEmployeeUdas(employeeId: number): Promise<any[]> {
    try {
      const response = await baseAPIService.get(`employee-management/employee/${employeeId}/udas`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || [];
    } catch (error: any) {
      console.error(`Error fetching UDAs for employee ${employeeId}:`, error);
      throw error;
    }
  }

  /**
   * Give an employee a UDA — creates the account, employee record and jurisdiction rows
   * they need there. Returns the new membership, whose `id` is the employee id to use for
   * any assignment at that UDA.
   *
   * Also what brings back a UDA that was deleted: the server finds the deactivated
   * membership, switches it on with its positions and service record intact, and returns
   * `reactivated: true`. Re-adding is the only way back — there is no restore call.
   */
  async grantEmployeeUda(employeeId: number, tenantCode: string): Promise<any> {
    try {
      const response = await baseAPIService.post(`employee-management/employee/${employeeId}/udas`, { tenant_code: tenantCode });
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data;
    } catch (error: any) {
      const msg = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to add the UDA';
      console.error(`Error granting UDA ${tenantCode} to employee ${employeeId}:`, error);
      throw new Error(msg);
    }
  }

  /**
   * Delete a UDA from an employee, as far as the operator is concerned. Under it the
   * account at that UDA is marked INACTIVE rather than removed, so they can no longer sign
   * in there and it stops appearing as somewhere they can be given a position, while the
   * employee record, positions, jurisdictions and role links are all kept. Call
   * grantEmployeeUda with the same UDA to bring it back with everything attached.
   *
   * The employee's last remaining UDA, and the UDA they are currently open at, are refused
   * by the server.
   */
  async revokeEmployeeUda(employeeId: number, tenantCode: string): Promise<any> {
    try {
      const response = await baseAPIService.delete(`employee-management/employee/${employeeId}/udas/${tenantCode}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data;
    } catch (error: any) {
      const msg = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to delete the UDA';
      console.error(`Error deleting UDA ${tenantCode} for employee ${employeeId}:`, error);
      throw new Error(msg);
    }
  }

  /**
   * Get all assignments for an employee
   * @param employeeId - Employee ID (the membership id at `tenantId`, when one is given)
   * @param tenantId - Read assignments at this UDA instead of the caller's own
   * @returns Promise with array of assignments
   */
  async getAssignmentsByEmployeeId(employeeId: number, tenantId?: string): Promise<any[]> {
    try {
      const qs = tenantId ? `?tenantid=${encodeURIComponent(tenantId)}` : '';
      const response = await baseAPIService.get(`employee-management/employee/${employeeId}/assignment${qs}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error: any) {
      console.error(`Error fetching assignments for employee ${employeeId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new assignment
   * @param assignmentData - Assignment data
   * @returns Promise with created assignment
   */
  async createAssignment(assignmentData: any): Promise<any> {
    try {
      const response = await baseAPIService.post('employee-management/assignment', assignmentData);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error('Error creating assignment:', error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to create assignment';
      throw new Error(errorMessage);
    }
  }

  /**
   * Update an existing assignment
   * @param assignmentId - Assignment ID
   * @param assignmentData - Updated assignment data
   * @returns Promise with updated assignment
   */
  async updateAssignment(assignmentId: number, assignmentData: any): Promise<any> {
    try {
      const response = await baseAPIService.put(`employee-management/assignment/${assignmentId}`, assignmentData);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error updating assignment ${assignmentId}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to update assignment';
      throw new Error(errorMessage);
    }
  }

  /**
   * Every live posting a person holds, across all their UDAs, with the nature of each and
   * the role it grants. Backs the hover card on a name in Audit, Activity and workflow
   * screens — see components/base/EmployeeNameHover.
   */
  async getEmployeePostings(employeeId: number | string): Promise<any[]> {
    try {
      const response = await baseAPIService.get(`employee-management/employee/${employeeId}/postings`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || [];
    } catch (error: any) {
      console.error(`Error fetching postings for employee ${employeeId}:`, error);
      throw error;
    }
  }

  /**
   * The nature-of-posting list — Regular/Substantive, In-charge, Transfer, Promotion,
   * Deputation, Acting. A shared statewide master, so it needs no UDA argument.
   */
  async getAssignmentTypes(): Promise<any[]> {
    try {
      const response = await baseAPIService.get('employee-management/assignment-types');
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || [];
    } catch (error: any) {
      console.error('Error fetching assignment types:', error);
      throw error;
    }
  }

  /**
   * Park an In-charge position the employee is not using, or bring it back.
   *
   * Only in-charge positions may be parked — the server refuses anything else, and refuses
   * to park the employee's last active position anywhere. Parking a UDA's only position
   * takes that UDA out of the employee's login picker without touching the membership.
   */
  async setAssignmentActive(assignmentId: number, active: boolean): Promise<any> {
    try {
      const response = await baseAPIService.put(`employee-management/assignment/${assignmentId}/active`, { active });
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error setting assignment ${assignmentId} active=${active}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message
        || (active ? 'Failed to make the position active' : 'Failed to make the position inactive');
      throw new Error(errorMessage);
    }
  }

  /**
   * Delete an assignment
   * @param assignmentId - Assignment ID
   * @returns Promise with deletion result
   */
  async deleteAssignment(assignmentId: number): Promise<any> {
    try {
      const response = await baseAPIService.delete(`employee-management/assignment/${assignmentId}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error deleting assignment ${assignmentId}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to delete assignment';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get all jurisdictions for an employee
   * @param employeeId - Employee ID
   * @returns Promise with array of jurisdictions
   */
  async getJurisdictionsByEmployeeId(employeeId: number): Promise<any[]> {
    try {
      const response = await baseAPIService.get(`employee-management/employee/${employeeId}/jurisdiction`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error: any) {
      console.error(`Error fetching jurisdictions for employee ${employeeId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new jurisdiction
   * @param jurisdictionData - Jurisdiction data
   * @returns Promise with created jurisdiction
   */
  async createJurisdiction(jurisdictionData: any): Promise<any> {
    try {
      const response = await baseAPIService.post('employee-management/jurisdiction', jurisdictionData);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error('Error creating jurisdiction:', error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to create jurisdiction';
      throw new Error(errorMessage);
    }
  }

  /**
   * Update an existing jurisdiction
   * @param jurisdictionId - Jurisdiction ID
   * @param jurisdictionData - Updated jurisdiction data
   * @returns Promise with updated jurisdiction
   */
  async updateJurisdiction(jurisdictionId: number, jurisdictionData: any): Promise<any> {
    try {
      const payload = { ...jurisdictionData, id: jurisdictionId };
      const response = await baseAPIService.post('employee-management/jurisdiction', payload);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error updating jurisdiction ${jurisdictionId}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to update jurisdiction';
      throw new Error(errorMessage);
    }
  }

  /**
   * Delete a jurisdiction
   * @param jurisdictionId - Jurisdiction ID
   * @returns Promise with deletion result
   */
  async deleteJurisdiction(jurisdictionId: number): Promise<any> {
    try {
      const response = await baseAPIService.delete(`employee-management/jurisdiction/${jurisdictionId}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error deleting jurisdiction ${jurisdictionId}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to delete jurisdiction';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get all hierarchy types for dropdown (uses lookup API)
   * @returns Promise with array of hierarchy types
   */
  async getHierarchyTypes(tenantId?: string): Promise<any[]> {
    try {
      const params: any = {};
      if (tenantId) params.tenantId = tenantId;
      const response = await baseAPIService.get('erp-admin/lookup/hierarchy-type', { params });
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching hierarchy types:', error);
      throw error;
    }
  }

  /**
   * Get boundary types by hierarchy type name filtered by user's hierarchy level
   * Only returns boundary types at or below the logged-in user's level
   * @param hierarchyTypeName - Hierarchy type name (e.g., "Administration")
   * @returns Promise with array of filtered boundary types
   */
  async getFilteredBndryTypsByHrchyTypId(hierarchyTypeId: number, tenantId?: string): Promise<any[]> {
    try {
      const params: any = {};
      if (tenantId) params.tenantId = tenantId;
      const response = await baseAPIService.get(`erp-admin/lookup/boundary-type/${hierarchyTypeId}`, { params });
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching boundary types by hierarchy type ID:', error);
      throw error;
    }
  }

  /**
   * Get boundary types by hierarchy type ID (uses master API for backward compatibility)
   * @param hierarchyTypeId - Hierarchy type ID
   * @returns Promise with array of boundary types
   */
  async getBoundaryTypesByHierarchyType(hierarchyTypeId: number): Promise<any[]> {
    try {
      const response = await baseAPIService.get(`erp-admin/boundary-type/hierarchy-type/${hierarchyTypeId}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching boundary types:', error);
      throw error;
    }
  }

  /**
   * Get all boundary types for filter dropdown
   * @returns Promise with array of all boundary types
   */
  async getAllBoundaryTypes(): Promise<any[]> {
    try {
      const response = await baseAPIService.get('erp-admin/boundary-type');
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching all boundary types:', error);
      throw error;
    }
  }

  /**
   * Get boundaries by boundary type for dropdown (uses lookup API)
   * @param boundaryTypeId - Boundary type ID
   * @param _ulbId - Optional ULB ID (not used - endpoint uses schema from JWT token context)
   * @returns Promise with array of boundaries
   */
  async getBoundariesByType(boundaryTypeId: number, _ulbId?: number, tenantId?: string): Promise<any[]> {
    try {
      const params: any = {};
      if (tenantId) params.tenantId = tenantId;
      const response = await baseAPIService.get(`erp-admin/lookup/boundary/${boundaryTypeId}`, { params });
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching boundaries:', error);
      throw error;
    }
  }

  /**
   * Get all roles
   * @returns Promise with array of roles
   */
  async getRoles(): Promise<any[]> {
    try {
      const response = await baseAPIService.get('erp-admin/lookup/roles');
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  }

  /**
   * Get gender options
   * @returns Promise with array of gender options
   */
  async getGenderOptions(): Promise<any[]> {
    try {
      const response = await baseAPIService.get('erp-admin/lookup/gender');
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching gender options:', error);
      throw error;
    }
  }

  /**
   * Get marital status options
   * @returns Promise with array of marital status options
   */
  async getMaritalStatusOptions(): Promise<any[]> {
    try {
      const response = await baseAPIService.get('erp-admin/lookup/marital-status');
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching marital status options:', error);
      throw error;
    }
  }

  /**
   * Get blood group options
   * @returns Promise with array of blood group options
   */
  async getBloodGroupOptions(): Promise<any[]> {
    try {
      const response = await baseAPIService.get('erp-admin/lookup/blood-group');
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching blood group options:', error);
      throw error;
    }
  }

  /**
   * Get religion options
   * @returns Promise with array of religion options
   */
  async getReligionOptions(tenantId?: string): Promise<any[]> {
    try {
      const params: any = {};
      if (tenantId) params.tenantId = tenantId;
      const response = await baseAPIService.get('erp-admin/lookup/religion', { params });
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching religion options:', error);
      throw error;
    }
  }

  /**
   * Get employee group options
   * @returns Promise with array of employee group options
   */
  async getEmployeeGroupOptions(tenantId?: string): Promise<any[]> {
    try {
      const params: any = {};
      if (tenantId) params.tenantId = tenantId;
      const response = await baseAPIService.get('erp-admin/lookup/employee-group', { params });
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching employee group options:', error);
      throw error;
    }
  }

  /**
   * Get category options
   * @returns Promise with array of category options
   */
  async getCategoryOptions(tenantId?: string): Promise<any[]> {
    try {
      const params: any = {};
      if (tenantId) params.tenantId = tenantId;
      const response = await baseAPIService.get('erp-admin/lookup/category', { params });
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching category options:', error);
      throw error;
    }
  }

  /**
   * Get employee status options
   * @returns Promise with array of employee status options
   */
  async getEmployeeStatusOptions(tenantId?: string): Promise<any[]> {
    try {
      const params: any = {};
      if (tenantId) params.tenantId = tenantId;
      const response = await baseAPIService.get('erp-admin/lookup/employee-status', { params });
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching employee status options:', error);
      throw error;
    }
  }

  /**
   * Get community options
   * @returns Promise with array of community options
   */
  async getCommunityOptions(): Promise<any[]> {
    try {
      const response = await baseAPIService.get('erp-admin/lookup/community');
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching community options:', error);
      throw error;
    }
  }

  /**
   * Get recruitment type options
   * @returns Promise with array of recruitment type options
   */
  async getRecruitmentTypeOptions(tenantId?: string): Promise<any[]> {
    try {
      const params: any = {};
      if (tenantId) params.tenantId = tenantId;
      const response = await baseAPIService.get('erp-admin/lookup/recruitment-type', { params });
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching recruitment type options:', error);
      throw error;
    }
  }

  /**
   * Get mother tongue/language options
   * @returns Promise with array of mother tongue options
   */
  async getMotherTongueOptions(tenantId?: string): Promise<any[]> {
    try {
      const params: any = {};
      if (tenantId) params.tenantId = tenantId;
      const response = await baseAPIService.get('erp-admin/lookup/mother-tongue', { params });
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching mother tongue options:', error);
      throw error;
    }
  }

  /**
   * Get recruitment mode options
   * @returns Promise with array of recruitment mode options
   */
  async getRecruitmentModeOptions(): Promise<any[]> {
    try {
      const response = await baseAPIService.get('erp-admin/lookup/recruitment-mode');
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching recruitment mode options:', error);
      throw error;
    }
  }

  /**
   * Get recruitment quota options
   * @returns Promise with array of recruitment quota options
   */
  async getRecruitmentQuotaOptions(): Promise<any[]> {
    try {
      const response = await baseAPIService.get('erp-admin/lookup/recruitment-quota');
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching recruitment quota options:', error);
      throw error;
    }
  }

  /**
   * Get all departments
   * @returns Promise with array of departments
   */
  async getDepartments(tenantId?: string, udaId?: number | null): Promise<any[]> {
    try {
      const params: any = {};
      if (tenantId) params.tenantId = tenantId;
      if (udaId !== undefined && udaId !== null) params.udaId = udaId;
      const response = await baseAPIService.get('erp-admin/lookup/department', { params });
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching departments:', error);
      throw error;
    }
  }

  /**
   * Get all designations
   * @param tenantId - Optional tenant ID override (for state-level users)
   * @returns Promise with array of designations
   */
  /**
   * @param departmentId - when set, returns only the designations linked to that
   *   department via ds_dept_dsgn_rel_t instead of every designation in the tenant.
   */
  async getDesignations(tenantId?: string, udaId?: number | null, departmentId?: number | string | null): Promise<any[]> {
    try {
      const params: any = {};
      if (tenantId) params.tenantId = tenantId;
      if (udaId !== undefined && udaId !== null) params.udaId = udaId;
      if (departmentId !== undefined && departmentId !== null && departmentId !== '') {
        params.departmentId = departmentId;
      }
      const response = await baseAPIService.get('erp-admin/lookup/designation', { params });
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching designations:', error);
      throw error;
    }
  }


  /**
   * Get all active banks for dropdown
   * @returns Promise with array of banks
   */
  async getBanks(): Promise<any[]> {
    try {
      const response = await baseAPIService.get('erp-admin/lookup/bank');
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching banks:', error);
      throw error;
    }
  }

  /**
   * Get all active bank branches for a given bank
   * @param bankId - Bank ID
   * @returns Promise with array of bank branches
   */
  async getBankBranchesByBank(bankId: number): Promise<any[]> {
    try {
      const response = await baseAPIService.get(`erp-admin/lookup/bank/${bankId}/branches`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching bank branches:', error);
      throw error;
    }
  }

  /**
   * Get default permissions (used when API doesn't return permissions)
   */
  getDefaultPermissions() {
    return {
      c_in: false,
      r_in: false,
      u_in: false,
      d_in: false,
      customPermissions: {},
    };
  }

  /**
   * Get employee types for filtering and dropdowns
   * @returns Promise with array of employee types
   */
  async getEmployeeTypes(tenantId?: string): Promise<any[]> {
    try {
      const params: any = {};
      if (tenantId) params.tenantId = tenantId;
      const response = await baseAPIService.get('erp-admin/lookup/employee-type', { params });
      if (response.data?.status != 200) {
        return [];
      }
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Error fetching employee types:', error);
      return [];
    }
  }

  /**
   * Upload employee photo
   * @param employeeId - Employee ID
   * @param file - Photo file to upload
   * @returns Promise with upload result including fileStoreId
   */
  async uploadPhoto(employeeId: number, file: File): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('photo', file);

      const response = await baseAPIService.post(
        `employee-management/employee/${employeeId}/upload-photo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (response.data?.status != 200) {
        throw new Error(response.data?.message || 'Failed to upload photo');
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error uploading photo for employee ${employeeId}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to upload photo';
      throw new Error(errorMessage);
    }
  }

  /**
   * Upload employee signature
   * @param employeeId - Employee ID
   * @param file - Signature file to upload
   * @returns Promise with upload result including fileStoreId
   */
  async uploadSignature(employeeId: number, file: File): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('signature', file);

      const response = await baseAPIService.post(
        `employee-management/employee/${employeeId}/upload-signature`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (response.data?.status != 200) {
        throw new Error(response.data?.message || 'Failed to upload signature');
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error uploading signature for employee ${employeeId}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to upload signature';
      throw new Error(errorMessage);
    }
  }
  /**
   * Upload one or more employee attachments (multipart, multiple files).
   * @param employeeId - Employee ID
   * @param files - Array of File objects
   */
  async uploadAttachments(employeeId: number, files: File[]): Promise<any> {
    try {
      const formData = new FormData();
      for (const f of files) formData.append('attachments', f);

      const response = await baseAPIService.post(
        `employee-management/employee/${employeeId}/upload-attachments`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (response.data?.status != 200) {
        throw new Error(response.data?.message || 'Failed to upload attachments');
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error('Error uploading attachments:', error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Failed to upload attachments');
    }
  }

  /**
   * List employee attachments — returns the file metadata on disk.
   */
  async listAttachments(employeeId: number): Promise<any[]> {
    try {
      const response = await baseAPIService.get(`employee-management/employee/${employeeId}/attachments`);
      if (response.data?.status != 200) throw new Error(response.data?.message || 'Failed to list attachments');
      return response.data?.data?.files || [];
    } catch (error: any) {
      console.error('Error listing attachments:', error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Failed to list attachments');
    }
  }

  /**
   * Get the download URL for a specific attachment, by its FileStore reference.
   */
  getAttachmentUrl(employeeId: number, ref: string): string {
    return `${env.API_BASE_URL}/apiv1/employee-management/employee/${employeeId}/attachments/${encodeURIComponent(ref)}`;
  }

  /**
   * Delete a specific attachment by its FileStore reference.
   */
  async deleteAttachment(employeeId: number, ref: string): Promise<void> {
    try {
      const response = await baseAPIService.delete(`employee-management/employee/${employeeId}/attachments/${encodeURIComponent(ref)}`);
      if (response.data?.status != 200) throw new Error(response.data?.message || 'Failed to delete attachment');
    } catch (error: any) {
      console.error('Error deleting attachment:', error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Failed to delete attachment');
    }
  }

  /** Delete the employee's photo file from the server (disk). */
  async deletePhoto(employeeId: number): Promise<void> {
    try {
      const response = await baseAPIService.delete(`employee-management/employee/${employeeId}/photo`);
      if (response.data?.status != 200) throw new Error(response.data?.message || 'Failed to delete photo');
    } catch (error: any) {
      console.error('Error deleting photo:', error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Failed to delete photo');
    }
  }

  /** Delete the employee's signature file from the server (disk). */
  async deleteSignature(employeeId: number): Promise<void> {
    try {
      const response = await baseAPIService.delete(`employee-management/employee/${employeeId}/signature`);
      if (response.data?.status != 200) throw new Error(response.data?.message || 'Failed to delete signature');
    } catch (error: any) {
      console.error('Error deleting signature:', error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Failed to delete signature');
    }
  }

  /**
   * Fetch which employee files exist on disk (photo / signature / attachments).
   * Used by the edit modal on open so signature/attachment tiles can show a
   * persistent "uploaded" status without relying on DB columns.
   * @returns { photo:{exists,fileName,url}, signature:{...}, attachments:{count,files[]} }
   */
  async getEmployeeFilesStatus(employeeId: number): Promise<any> {
    try {
      const response = await baseAPIService.get(`employee-management/employee/${employeeId}/files-status`);
      if (response.data?.status != 200) throw new Error(response.data?.message || 'Failed to fetch file status');
      return response.data?.data || null;
    } catch (error: any) {
      console.error('Error fetching employee files status:', error);
      return null; // non-fatal — tiles just fall back to empty state
    }
  }

  // =====================================================
  // ASSIGNMENT DOCUMENT METHODS (disk-only — files live at
  // <server>/uploads/assignment-documents/<assignmentId>/<originalName>)
  // =====================================================

  /**
   * Upload one or more documents for an assignment.
   * @param assignmentId - Assignment ID (must already exist on server)
   * @param files - Array of File objects (multipart, multiple allowed)
   */
  async uploadAssignmentDocuments(assignmentId: number, files: File[]): Promise<any> {
    try {
      const formData = new FormData();
      for (const f of files) formData.append('documents', f);

      const response = await baseAPIService.post(
        `employee-management/assignment/${assignmentId}/upload-documents`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (response.data?.status != 200) {
        throw new Error(response.data?.message || 'Failed to upload documents');
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error('Error uploading assignment documents:', error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Failed to upload documents');
    }
  }

  /**
   * List documents for a given assignment.
   * @returns Array of { fileName, size, modifiedAt, fileUrl }
   */
  async listAssignmentDocuments(assignmentId: number): Promise<any[]> {
    try {
      const response = await baseAPIService.get(`employee-management/assignment/${assignmentId}/documents`);
      if (response.data?.status != 200) throw new Error(response.data?.message || 'Failed to list documents');
      return response.data?.data?.files || [];
    } catch (error: any) {
      console.error('Error listing assignment documents:', error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Failed to list documents');
    }
  }

  /**
   * Get the download URL for a specific assignment document.
   */
  getAssignmentDocumentUrl(assignmentId: number, fileName: string): string {
    return `${env.API_BASE_URL}/apiv1/employee-management/assignment/${assignmentId}/documents/${encodeURIComponent(fileName)}`;
  }

  /**
   * Delete a specific assignment document.
   */
  async deleteAssignmentDocument(assignmentId: number, fileName: string): Promise<void> {
    try {
      const response = await baseAPIService.delete(`employee-management/assignment/${assignmentId}/documents/${encodeURIComponent(fileName)}`);
      if (response.data?.status != 200) throw new Error(response.data?.message || 'Failed to delete document');
    } catch (error: any) {
      console.error('Error deleting assignment document:', error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Failed to delete document');
    }
  }

  /**
   * Get employee photo URL
   * @param employeeId - Employee ID
   * @returns Photo URL string
   */
  getPhotoUrl(employeeId: number): string {
    return `${env.API_BASE_URL}/apiv1/employee-management/employee/${employeeId}/photo`;
  }

  /**
   * Get employee signature URL
   * @param employeeId - Employee ID
   * @returns Signature URL string
   */
  getSignatureUrl(employeeId: number): string {
    return `${env.API_BASE_URL}/apiv1/employee-management/employee/${employeeId}/signature`;
  }

  /**
   * Fetch a protected image (photo/signature) through the authenticated axios
   * client and return an object URL for an <img src>. A plain <img src=endpoint>
   * (or even native fetch) is unreliable cross-origin in dev (app :3000, API
   * :4901) — the auth header/cookie handling differs. baseAPIService is the same
   * client every working API call uses, so this resolves protected images
   * reliably. Returns null on failure (caller shows the fallback icon).
   * @param relativePath e.g. `employee-management/employee/123/photo`
   */
  async fetchImageObjectUrl(relativePath: string): Promise<string | null> {
    try {
      const response = await baseAPIService.get(relativePath, { responseType: 'blob' });
      const blob = response?.data;
      if (!blob || !(blob instanceof Blob) || blob.size === 0) return null;
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('fetchImageObjectUrl failed for', relativePath, error);
      return null;
    }
  }

  // =====================================================
  // ROLE METHODS (read-only — for Role tab in employee modal)
  // =====================================================

  async getUserRoles(userId: number): Promise<any[]> {
    try {
      const response = await baseAPIService.get(`erp-admin/role/user/${userId}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message || 'Failed to fetch user roles');
      }
      return response.data?.data || [];
    } catch (error: any) {
      console.error(`Error fetching roles for user ${userId}:`, error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Failed to fetch user roles');
    }
  }

  async getRoleMenuItems(roleId: number): Promise<any[]> {
    try {
      const response = await baseAPIService.get(`erp-admin/role/${roleId}/menu-items`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message || 'Failed to fetch role menu items');
      }
      return response.data?.data || [];
    } catch (error: any) {
      console.error(`Error fetching menu items for role ${roleId}:`, error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Failed to fetch role menu items');
    }
  }

  async getRoleSetupOptions(roleId: number): Promise<any[]> {
    try {
      const response = await baseAPIService.get(`erp-admin/role/${roleId}/setup-options`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message || 'Failed to fetch role setup options');
      }
      return response.data?.data || [];
    } catch (error: any) {
      console.error(`Error fetching setup options for role ${roleId}:`, error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Failed to fetch role setup options');
    }
  }

  async getRoleReports(roleId: number): Promise<any[]> {
    try {
      const response = await baseAPIService.get(`erp-admin/role/${roleId}/reports`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message || 'Failed to fetch role reports');
      }
      return response.data?.data || [];
    } catch (error: any) {
      console.error(`Error fetching reports for role ${roleId}:`, error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Failed to fetch role reports');
    }
  }

  async getRoleTablePermissions(roleId: number): Promise<any[]> {
    try {
      const response = await baseAPIService.get(`erp-auth/role-table-permissions`, { params: { roleId } });
      if (response.data?.status != 200) {
        throw new Error(response.data?.message || 'Failed to fetch role table permissions');
      }
      return response.data?.data || [];
    } catch (error: any) {
      console.error(`Error fetching table permissions for role ${roleId}:`, error);
      throw new Error(error?.response?.data?.err_message || error?.message || 'Failed to fetch role table permissions');
    }
  }

  // =====================================================
  // SERVICE HISTORY METHODS
  // =====================================================

  /**
   * Get all service history records for an employee
   * @param employeeId - Employee ID
   * @returns Promise with array of service history records
   */
  async getServiceHistoryByEmployeeId(employeeId: number): Promise<{ data: any[]; perm?: any }> {
    try {
      const response = await baseAPIService.get(`employee-management/service-history/employee/${employeeId}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return { data: response.data?.data || [], perm: response.data?.perm };
    } catch (error: any) {
      console.error(`Error fetching service history for employee ${employeeId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new service history record
   * @param serviceHistoryData - Service history data
   * @returns Promise with created service history record
   */
  async createServiceHistory(serviceHistoryData: any): Promise<any> {
    try {
      const response = await baseAPIService.post('employee-management/service-history', serviceHistoryData);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error('Error creating service history:', error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to create service history';
      throw new Error(errorMessage);
    }
  }

  /**
   * Update an existing service history record
   * @param id - Service history ID
   * @param serviceHistoryData - Updated service history data
   * @returns Promise with updated service history record
   */
  async updateServiceHistory(id: number, serviceHistoryData: any): Promise<any> {
    try {
      const response = await baseAPIService.put(`employee-management/service-history/${id}`, serviceHistoryData);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error updating service history ${id}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to update service history';
      throw new Error(errorMessage);
    }
  }

  /**
   * Delete a service history record
   * @param id - Service history ID
   * @returns Promise with deletion result
   */
  async deleteServiceHistory(id: number): Promise<any> {
    try {
      const response = await baseAPIService.delete(`employee-management/service-history/${id}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error deleting service history ${id}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to delete service history';
      throw new Error(errorMessage);
    }
  }

  // =====================================================
  // PROBATION METHODS
  // =====================================================

  /**
   * Get all probation records for an employee
   * @param employeeId - Employee ID
   * @returns Promise with array of probation records
   */
  async getProbationByEmployeeId(employeeId: number): Promise<{ data: any[]; perm?: any }> {
    try {
      const response = await baseAPIService.get(`employee-management/probation/employee/${employeeId}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return { data: response.data?.data || [], perm: response.data?.perm };
    } catch (error: any) {
      console.error(`Error fetching probation records for employee ${employeeId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new probation record
   * @param probationData - Probation data
   * @returns Promise with created probation record
   */
  async createProbation(probationData: any): Promise<any> {
    try {
      const response = await baseAPIService.post('employee-management/probation', probationData);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error('Error creating probation record:', error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to create probation record';
      throw new Error(errorMessage);
    }
  }

  /**
   * Update an existing probation record
   * @param id - Probation ID
   * @param probationData - Updated probation data
   * @returns Promise with updated probation record
   */
  async updateProbation(id: number, probationData: any): Promise<any> {
    try {
      const response = await baseAPIService.put(`employee-management/probation/${id}`, probationData);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error updating probation record ${id}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to update probation record';
      throw new Error(errorMessage);
    }
  }

  /**
   * Delete a probation record
   * @param id - Probation ID
   * @returns Promise with deletion result
   */
  async deleteProbation(id: number): Promise<any> {
    try {
      const response = await baseAPIService.delete(`employee-management/probation/${id}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error deleting probation record ${id}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to delete probation record';
      throw new Error(errorMessage);
    }
  }

  // =====================================================
  // REGULARISATION METHODS
  // =====================================================

  /**
   * Get all regularisation records for an employee
   * @param employeeId - Employee ID
   * @returns Promise with array of regularisation records
   */
  async getRegularisationByEmployeeId(employeeId: number): Promise<{ data: any[]; perm?: any }> {
    try {
      const response = await baseAPIService.get(`employee-management/regularisation/employee/${employeeId}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return { data: response.data?.data || [], perm: response.data?.perm };
    } catch (error: any) {
      console.error(`Error fetching regularisation records for employee ${employeeId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new regularisation record
   * @param regularisationData - Regularisation data
   * @returns Promise with created regularisation record
   */
  async createRegularisation(regularisationData: any): Promise<any> {
    try {
      const response = await baseAPIService.post('employee-management/regularisation', regularisationData);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error('Error creating regularisation record:', error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to create regularisation record';
      throw new Error(errorMessage);
    }
  }

  /**
   * Update an existing regularisation record
   * @param id - Regularisation ID
   * @param regularisationData - Updated regularisation data
   * @returns Promise with updated regularisation record
   */
  async updateRegularisation(id: number, regularisationData: any): Promise<any> {
    try {
      const response = await baseAPIService.put(`employee-management/regularisation/${id}`, regularisationData);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error updating regularisation record ${id}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to update regularisation record';
      throw new Error(errorMessage);
    }
  }

  /**
   * Delete a regularisation record
   * @param id - Regularisation ID
   * @returns Promise with deletion result
   */
  async deleteRegularisation(id: number): Promise<any> {
    try {
      const response = await baseAPIService.delete(`employee-management/regularisation/${id}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error deleting regularisation record ${id}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to delete regularisation record';
      throw new Error(errorMessage);
    }
  }

  // =====================================================
  // EDUCATIONAL QUALIFICATION METHODS
  // =====================================================

  /**
   * Get all educational qualifications for an employee
   * @param employeeId - Employee ID
   * @returns Promise with array of educational qualification records
   */
  async getEducationalQualificationByEmployeeId(employeeId: number): Promise<{ data: any[]; perm?: any }> {
    try {
      const response = await baseAPIService.get(`employee-management/educational-qualification/employee/${employeeId}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return { data: response.data?.data || [], perm: response.data?.perm };
    } catch (error: any) {
      console.error(`Error fetching educational qualifications for employee ${employeeId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new educational qualification record
   * @param qualificationData - Educational qualification data
   * @returns Promise with created educational qualification record
   */
  async createEducationalQualification(qualificationData: any): Promise<any> {
    try {
      const response = await baseAPIService.post('employee-management/educational-qualification', qualificationData);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error('Error creating educational qualification:', error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to create educational qualification';
      throw new Error(errorMessage);
    }
  }

  /**
   * Update an existing educational qualification record
   * @param id - Educational qualification ID
   * @param qualificationData - Updated educational qualification data
   * @returns Promise with updated educational qualification record
   */
  async updateEducationalQualification(id: number, qualificationData: any): Promise<any> {
    try {
      const response = await baseAPIService.put(`employee-management/educational-qualification/${id}`, qualificationData);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error updating educational qualification ${id}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to update educational qualification';
      throw new Error(errorMessage);
    }
  }

  /**
   * Delete an educational qualification record
   * @param id - Educational qualification ID
   * @returns Promise with deletion result
   */
  async deleteEducationalQualification(id: number): Promise<any> {
    try {
      const response = await baseAPIService.delete(`employee-management/educational-qualification/${id}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error deleting educational qualification ${id}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to delete educational qualification';
      throw new Error(errorMessage);
    }
  }

  // =====================================================
  // TECHNICAL QUALIFICATION METHODS
  // =====================================================

  /**
   * Get all technical qualifications for an employee
   * @param employeeId - Employee ID
   * @returns Promise with array of technical qualification records
   */
  async getTechnicalQualificationByEmployeeId(employeeId: number): Promise<{ data: any[]; perm?: any }> {
    try {
      const response = await baseAPIService.get(`employee-management/technical-qualification/employee/${employeeId}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return { data: response.data?.data || [], perm: response.data?.perm };
    } catch (error: any) {
      console.error(`Error fetching technical qualifications for employee ${employeeId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new technical qualification record
   * @param qualificationData - Technical qualification data
   * @returns Promise with created technical qualification record
   */
  async createTechnicalQualification(qualificationData: any): Promise<any> {
    try {
      const response = await baseAPIService.post('employee-management/technical-qualification', qualificationData);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error('Error creating technical qualification:', error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to create technical qualification';
      throw new Error(errorMessage);
    }
  }

  /**
   * Update an existing technical qualification record
   * @param id - Technical qualification ID
   * @param qualificationData - Updated technical qualification data
   * @returns Promise with updated technical qualification record
   */
  async updateTechnicalQualification(id: number, qualificationData: any): Promise<any> {
    try {
      const response = await baseAPIService.put(`employee-management/technical-qualification/${id}`, qualificationData);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error updating technical qualification ${id}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to update technical qualification';
      throw new Error(errorMessage);
    }
  }

  /**
   * Delete a technical qualification record
   * @param id - Technical qualification ID
   * @returns Promise with deletion result
   */
  async deleteTechnicalQualification(id: number): Promise<any> {
    try {
      const response = await baseAPIService.delete(`employee-management/technical-qualification/${id}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error deleting technical qualification ${id}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to delete technical qualification';
      throw new Error(errorMessage);
    }
  }

  // =====================================================
  // DEPARTMENTAL TEST METHODS
  // =====================================================

  /**
   * Get all departmental tests for an employee
   * @param employeeId - Employee ID
   * @returns Promise with array of departmental test records
   */
  async getDepartmentalTestByEmployeeId(employeeId: number): Promise<{ data: any[]; perm?: any }> {
    try {
      const response = await baseAPIService.get(`employee-management/departmental-test/employee/${employeeId}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return { data: response.data?.data || [], perm: response.data?.perm };
    } catch (error: any) {
      console.error(`Error fetching departmental tests for employee ${employeeId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new departmental test record
   * @param testData - Departmental test data
   * @returns Promise with created departmental test record
   */
  async createDepartmentalTest(testData: any): Promise<any> {
    try {
      const response = await baseAPIService.post('employee-management/departmental-test', testData);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error('Error creating departmental test:', error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to create departmental test';
      throw new Error(errorMessage);
    }
  }

  /**
   * Update an existing departmental test record
   * @param id - Departmental test ID
   * @param testData - Updated departmental test data
   * @returns Promise with updated departmental test record
   */
  async updateDepartmentalTest(id: number, testData: any): Promise<any> {
    try {
      const response = await baseAPIService.put(`employee-management/departmental-test/${id}`, testData);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error updating departmental test ${id}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to update departmental test';
      throw new Error(errorMessage);
    }
  }

  /**
   * Delete a departmental test record
   * @param id - Departmental test ID
   * @returns Promise with deletion result
   */
  async deleteDepartmentalTest(id: number): Promise<any> {
    try {
      const response = await baseAPIService.delete(`employee-management/departmental-test/${id}`);
      if (response.data?.status != 200) {
        throw new Error(response.data?.message);
      }
      return response.data?.data || response.data;
    } catch (error: any) {
      console.error(`Error deleting departmental test ${id}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to delete departmental test';
      throw new Error(errorMessage);
    }
  }

  /**
   * Get audit history for an employee — all changes recorded in adt_data_hst_t
   * across the employee record itself (egeis_employee/eg_user) and every
   * audit-enabled child table (assignments, jurisdictions, qualifications, etc.).
   */
  async getEmployeeAuditHistory(
    employeeId: number,
    options?: { limit?: number; offset?: number }
  ): Promise<{ employeeId: number; count: number; history: AuditHistoryEntry[] }> {
    try {
      const params: any = {};
      if (options?.limit  !== undefined) params.limit  = options.limit;
      if (options?.offset !== undefined) params.offset = options.offset;
      const response: any = await baseAPIService.get(
        `employee-management/employee/${employeeId}/audit-history`,
        { params }
      );
      return response.data?.data || { employeeId, count: 0, history: [] };
    } catch (error: any) {
      console.error(`Error fetching audit history for employee ${employeeId}:`, error);
      const errorMessage = error?.response?.data?.err_message || error?.response?.data?.message || error?.message || 'Failed to fetch audit history';
      throw new Error(errorMessage);
    }
  }
}

// Audit history shape mirrors the backend's getEmployeeAuditHistoryMdl output.
export interface AuditHistoryEntry {
  audit_id: number;
  table_name: string;
  table_display_name: string;
  record_id: number;
  operation: 'INSERT' | 'UPDATE' | 'DELETE' | string;
  operation_ts: string;
  changed_fields: string[] | Record<string, any> | null;
  changed_count: number | null;
  old_values: Record<string, any> | null;
  new_values: Record<string, any> | null;
  change_reason: string | null;
  change_source: string | null;
  actor_user_id: number;
  actor_name: string | null;
  api_endpoint: string | null;
  api_method: string | null;
}

// Create and export a default instance
const empManagementService = new EmpManagementService();
export default empManagementService;

// Export types
export type { UserFilters, Stats, UserData };

